<template>
  <div class="custom-modal-wrap overflow-y-auto  overflow-x-hidden fixed top-0 right-0 left-0 z-[100] md:inset-0 h-modal md:h-full justify-center items-center flex">
    <div class="relative  pt-[80px] pb-[80px]  grid items-center p-4 w-full h-full md:h-[100vh] max-w-[820px]">
      <div class="relative rounded-xl shadow bg-theme-modalbg">
        <div class="grid grid-cols-1 lg:grid-cols-1 gap-3" style="font-family: Arial">
          <div class="text-white flex-1 col-span-1 border-0 rounded-t-xl overflow-hidden">
            <div class="w-[800px] rounded-2xl shadow-[0px_25px_50px_-12px_rgba(0,0,0,1.00)] inline-flex flex-col justify-start items-center overflow-hidden">
              <div class="self-stretch px-6 py-4 bg-green-500 inline-flex justify-start items-start gap-6">
                <div class="flex-1 self-stretch inline-flex flex-col justify-center items-center gap-0.5">
                  <div class="self-stretch justify-center text-white text-xl font-bold font-['Roboto'] leading-6 tracking-tight line-clamp-1">Manage Locations for [{{ profile?.name || 'Profile' }}]</div>
                  <div class="self-stretch justify-center text-white text-sm font-normal font-['Roboto'] leading-4">Select which locations should use this business branding profile. Locations can only be assigned to one branding at a time.</div>
                </div>
                <div class="flex justify-end items-center gap-4">
                  <button @click="closePop" data-configuration="Text-L" data-fill="True" data-show-icon="False" data-state="Default" class="w-24 h-11 p-4 rounded outline outline-1 outline-offset-[-1px] outline-white flex justify-center items-center gap-2 cursor-pointer">
                    <div class="text-center justify-center text-white text-sm font-medium font-['Roboto'] uppercase leading-4 tracking-widest">CANCEL</div>
                  </button>
                  <button
                    :disabled="!selectedLocations.length"
                    @click="assignLocations"
                    data-configuration="Text-L"
                    data-fill="True"
                    data-show-icon="False"
                    data-state="Default"
                    class="w-24 h-11 p-4 bg-white rounded flex justify-center items-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <div class="text-center justify-center text-green-500 text-sm font-medium font-['Roboto'] uppercase leading-4 tracking-widest">Assign</div>
                  </button>
                </div>
              </div>
              <div class="self-stretch px-6 pt-6 pb-8 bg-zinc-800 flex flex-col justify-start items-start gap-4 overflow-hidden">
                <div v-if="loading" class="self-stretch flex justify-center py-8">
                  <block-loading />
                </div>
                <template v-else>
                  <div class="self-stretch justify-center text-white text-base font-bold font-['Roboto'] leading-5 tracking-tight">{{ selectedCount }} locations selected</div>
                  <div v-if="!locations.length" class="self-stretch justify-center text-neutral-400 text-sm py-4">No locations found.</div>
                  <div v-else class="max-h-[350px] overflow-y-auto">
                  <div class="grid grid-cols-2 self-stretch inline-flex justify-start items-start gap-4 flex-wrap">
                    <div
                      v-for="location in locations"
                      :key="location.id"
                      class="self-stretch p-2 rounded-md outline outline-1 outline-offset-[-1px] inline-flex justify-start items-start gap-2 min-w-[280px]"
                      :class="selectedLocations.includes(location.id)?'bg-green-500/20 outline-green-500':'bg-zinc-800 outline-zinc-700'"
                    >
                      <div class="admin-table flex flex-row w-full justify-between">
                        <label class="cursor-pointer text-sm min-w-[200px] self-stretch flex justify-start items-start gap-2">
                          <input
                            :id="`location-${location.id}`"
                            type="checkbox"
                            :value="location.id"
                            class="mr-3 mt-0.5 w-[20px] h-[20px] focus:ring-0"
                            :checked="selectedLocations.includes(location.id)"
                            @change="toggleLocation(location.id)"
                          />
                          <div class="flex-1 inline-flex flex-col justify-start items-start gap-0.5">
                            <div class="self-stretch justify-center text-white text-base font-medium font-['Roboto'] leading-6 line-clamp-1">{{ location.name }}</div>
                            <div class="self-stretch justify-center text-neutral-400 text-xs font-normal font-['Roboto'] leading-4 line-clamp-1">{{ formatAddress(location) }}</div>
                          </div>
                        </label>
                        <div class="min-w-[120px] p-1 bg-neutral-900 rounded-md inline-flex flex-col justify-start items-center gap-0.5">
                          <div class="text-center justify-center text-neutral-400 text-xs font-normal font-['Roboto'] leading-4">Currently:</div>
                          <div class="text-center justify-center text-white text-xs font-normal font-['Roboto'] leading-4 line-clamp-1">{{ location.businessBrandProfile?.name || '—' }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import {
    defineComponent,
    ref,
    computed,
    onMounted,
    watch
  } from "vue"
  import {
    mapActions
  } from "vuex";
  import { useSnackbar } from "vue3-snackbar"
  import LocationsApis from "@/controllers/LocationsApis"
  import BrandingProfileApis from "@/controllers/BrandingProfileApis"
  import Helpers from "@/helpers"

  interface BrandingProfile {
    id?: number
    name: string
    domain?: string
    googleAnalyticsKey?: string
    logo?: string
    favicon?: string
    primaryColor?: string
    secondaryColor?: string
  }

  interface LocationItem {
    id: string
    name: string
    address1?: string
    address2?: string
    city?: string
    province?: string
    postalCode?: string
    country?: string
    businessBrandProfileId?: number
    [key: string]: unknown
  }

  export default defineComponent({
    props: {
      profile: {
        type: Object as () => BrandingProfile | null,
        default: null
      }
    },
    setup(props) {
      const snackbar = useSnackbar()
      const locations = ref<LocationItem[]>([])
      const loading = ref(false)
      const selectedLocations = ref<string[]>([])
      const selectedCount = computed(() => selectedLocations.value.length)

      function formatAddress(loc: LocationItem): string {
        const parts = [
          loc.address1,
          loc.city,
          loc.province,
          loc.postalCode,
          loc.country
        ].filter(Boolean) as string[]
        return parts.join(", ") || "—"
      }

      function toggleLocation(locationId: string) {
        const idx = selectedLocations.value.indexOf(locationId)
        if (idx === -1) {
          selectedLocations.value = [...selectedLocations.value, locationId]
        } else {
          selectedLocations.value = selectedLocations.value.filter((id) => id !== locationId)
        }
      }

      async function fetchLocations() {
        if (!props.profile?.id) return
        loading.value = true
        try {
          const params = `includeBusinessBrandProfile=1`
          const res = await LocationsApis.fetchAllLocations(params)
          const payload = res?.data?.payload
          locations.value = Array.isArray(payload) ? payload : []
        } catch {
          locations.value = []
        } finally {
          loading.value = false
        }
      }

      async function fetchSelectedLocations() {
        if (!props.profile?.id) return
        try {
          const res = await BrandingProfileApis.businessBrandingLocations(props.profile.id)
          const payload = res?.data?.payload
          const list = Array.isArray(payload) ? payload : []
          selectedLocations.value = list.map((loc: { id?: string | number }) => String(loc?.id ?? '')).filter(Boolean)
        } catch {
          selectedLocations.value = []
        }
      }

      onMounted(async () => {
        await fetchLocations()
        await fetchSelectedLocations()
      })

      return {
        locations,
        loading,
        selectedLocations,
        selectedCount,
        formatAddress,
        toggleLocation,
        snackbar
      }
    },
    methods: {
      closePop() {
        this.$emit("close")
      },
      async assignLocations() {
        const brandId = this.profile?.id
        if (brandId == null || !this.selectedLocations.length) return
        this.va_spinner(true)
        try {
          const res = await BrandingProfileApis.syncLocationsToBrandingProfile(brandId, {
            locationIds: this.selectedLocations
          })
          if (res?.status === 201) {
            const message = res?.data?.message ?? res?.data?.payload ?? "Locations synced successfully"
            this.snackbar.add({
              type: "info",
              text: typeof message === "string" ? message : JSON.stringify(message),
              duration: 3000
            })
            this.$emit("close", { reload: true })
          } else {
            this.snackbar.add({
              type: "error",
              text: Helpers.catchErrorMessage(res?.response?.data) ?? "Failed to sync locations",
              duration: 3000
            })
          }
        } catch (err: unknown) {
          const msg = err && typeof err === "object" && "response" in err
            ? Helpers.catchErrorMessage((err as { response?: { data?: unknown } }).response?.data)
            : "Failed to sync locations"
          this.snackbar.add({
            type: "error",
            text: msg ?? "Failed to sync locations",
            duration: 3000
          })
        } finally {
          this.va_spinner(false)
        }
      },
      ...mapActions({
        va_spinner: 'spinner/setSpinner',
      })
    }
  })
</script>